import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Google as GoogleIcon, Facebook as FacebookIcon, Visibility, VisibilityOff, StorefrontOutlined } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import PhoneInput from '../common/PhoneInput';
import { errorMessage } from '../../utils/errorMessage';
import AuthPageShell from './AuthPageShell';

interface RegisterForm {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite: string;
  referralCode: string;
}

const ModernRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    referralCode: new URLSearchParams(location.search).get('ref')?.trim().toUpperCase() || '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const steps = [
    t('auth.personalInfo'),
    t('auth.accountDetails'),
    t('auth.companyInfo'),
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateStep = (step: number): boolean => {
    setError('');
    
    switch (step) {
      case 0:
        if (!form.firstName.trim()) {
          setError(t('auth.firstName') + ' — ' + t('auth.fieldRequired'));
          return false;
        }
        if (!form.lastName.trim()) {
          setError(t('auth.lastName') + ' — ' + t('auth.fieldRequired'));
          return false;
        }
        break;
        
      case 1:
        if (!form.username.trim()) {
          setError(t('auth.username') + ' — ' + t('auth.fieldRequired'));
          return false;
        }
        if (form.username.length < 3) {
          setError(t('auth.usernameMinLength'));
          return false;
        }
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          setError(t('auth.emailInvalid'));
          return false;
        }
        if (!form.phoneNumber.trim()) {
          setError(t('auth.phoneNumberRequired'));
          return false;
        }
        if (form.phoneNumber.length < 10) {
          setError(t('auth.phoneNumberInvalid'));
          return false;
        }
        if (!form.password) {
          setError(t('auth.password') + ' — ' + t('auth.fieldRequired'));
          return false;
        }
        if (form.password.length < 6) {
          setError(t('auth.passwordMinLength'));
          return false;
        }
        if (form.password !== form.confirmPassword) {
          setError(t('auth.passwordMismatch'));
          return false;
        }
        break;
        
      case 2:
        if (!form.companyName.trim()) {
          setError(t('auth.companyName') + ' — ' + t('auth.fieldRequired'));
          return false;
        }
        if (form.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.companyEmail)) {
          setError(t('auth.emailInvalid'));
          return false;
        }
        break;
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/');
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await apiClient.register({
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password,
        companyName: form.companyName,
        companyAddress: form.companyAddress || undefined,
        companyPhone: form.companyPhone || undefined,
        companyEmail: form.companyEmail || undefined,
        companyWebsite: form.companyWebsite || undefined,
        referralCode: form.referralCode || undefined,
      });

      // Registration successful, redirect to email verification page
      navigate('/verify-email', { 
        state: { 
          email: form.email,
          message: t('auth.registrationNextStep')
        } 
      });
    } catch (err: unknown) {
      setError(errorMessage(err, t('auth.registrationFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = (provider: 'google' | 'facebook') => {
    const message = provider === 'google' 
      ? t('auth.googleLoginComingSoon')
      : t('auth.facebookLoginComingSoon');
    setError(message);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              margin="normal"
              fullWidth
              id="firstName"
              label={t('auth.firstName')}
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={form.firstName}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="lastName"
              label={t('auth.lastName')}
              name="lastName"
              autoComplete="family-name"
              value={form.lastName}
              onChange={handleChange}
              disabled={isLoading}
            />
          </>
        );
        
      case 1:
        return (
          <>
            <TextField
              margin="normal"
              fullWidth
              id="username"
              label={t('auth.username')}
              name="username"
              autoComplete="username"
              value={form.username}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.usernameMinLength')}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.checkEmail')}
            />
            
            <PhoneInput
              value={form.phoneNumber}
              onChange={(value) => setForm({ ...form, phoneNumber: value })}
              label={t('auth.phoneNumber')}
              disabled={isLoading}
              fullWidth
            />
            
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
              helperText={t('auth.passwordMinLength')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              margin="normal"
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        );
        
      case 2:
        return (
          <>
            <TextField
              margin="normal"
              fullWidth
              id="companyName"
              label={t('auth.companyName')}
              name="companyName"
              autoComplete="organization"
              value={form.companyName}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyAddress"
              label={`${t('auth.companyAddress')} (${t('common.optional')})`}
              name="companyAddress"
              autoComplete="street-address"
              value={form.companyAddress}
              onChange={handleChange}
              disabled={isLoading}
              multiline
              rows={2}
            />
            
            <PhoneInput
              value={form.companyPhone}
              onChange={(value) => setForm({ ...form, companyPhone: value })}
              label={`${t('auth.companyPhone')} (${t('common.optional')})`}
              disabled={isLoading}
              fullWidth
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyEmail"
              label={`${t('auth.companyEmail')} (${t('common.optional')})`}
              name="companyEmail"
              type="email"
              autoComplete="email"
              value={form.companyEmail}
              onChange={handleChange}
              disabled={isLoading}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="companyWebsite"
              label={`${t('auth.companyWebsite')} (${t('common.optional')})`}
              name="companyWebsite"
              type="url"
              autoComplete="url"
              value={form.companyWebsite}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="https://"
            />

            <TextField
              margin="normal"
              fullWidth
              id="referralCode"
              label={`${t('auth.referralCode')} (${t('common.optional')})`}
              name="referralCode"
              value={form.referralCode}
              onChange={handleChange}
              disabled={isLoading}
              inputProps={{ maxLength: 12, style: { textTransform: 'uppercase' } }}
              helperText={t('auth.referralCodeHint')}
            />
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <AuthPageShell title={t('auth.createAccount')} description={t('auth.registrationDescription')} icon={<StorefrontOutlined />} wide>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.step')} {activeStep + 1} {t('auth.of')} {steps.length}
          </Typography>

          {/* OAuth Buttons - Only show on first step */}
          {activeStep === 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleOAuthLogin('google')}
                  disabled={isLoading}
                >
                  {t('auth.continueWithGoogle')}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FacebookIcon />}
                  onClick={() => handleOAuthLogin('facebook')}
                  disabled={isLoading}
                >
                  {t('auth.continueWithFacebook')}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.orContinueWith')}
                </Typography>
              </Divider>
            </>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3.5, '& .MuiStepLabel-label': { fontSize: { xs: 11, sm: 13 } } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box component="form" noValidate sx={{ mt: 1, display: 'grid', gap: .25 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              onClick={handleBack}
              disabled={isLoading}
            >
              {activeStep === 0 ? t('auth.backToHome') : t('auth.back')}
            </Button>
            
            <Box sx={{ flex: '1 1 auto' }} />
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : t('auth.finish')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading}
              >
                {t('auth.next')}
              </Button>
            )}
          </Box>

          <Box textAlign="center" sx={{ mt: 3 }}>
            <Typography variant="body2">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link 
                to="/login" 
                style={{ 
                  textDecoration: 'none',
                  color: '#64B5F6',
                  fontWeight: 500,
                }}
              >
                {t('auth.login')}
              </Link>
            </Typography>
          </Box>
    </AuthPageShell>
  );
};

export default ModernRegister;
